<template>
  <header class="bg-white border-b border-gray-200 sticky top-0 z-50">
    <div class="pr-0 pl-6">
      <div class="flex justify-between items-center h-[4.5rem]">
        <RouterLink to="/" class="flex items-center gap-2">
          <img
            :src="logoUrl"
            alt="エンジニアファクトリー"
            class="h-[48px] w-auto"
          />
        </RouterLink>

        <nav class="flex items-center ml-auto relative">
          <RouterLink
            v-for="item in menuItems"
            :key="item.path"
            :to="item.path"
            class="hidden md:flex items-center justify-center w-[7rem] h-[4.5rem] text-sm font-bold transition-colors duration-150"
            :class="
              isActive(item.path)
                ? 'bg-primary text-white hover:bg-primary/90'
                : 'text-gray-800 hover:bg-gray-100'
            "
          >
            <i :class="item.icon + ' mr-2'"></i>{{ item.label }}
          </RouterLink>

          <div class="relative">
            <button
              class="hidden md:flex items-center justify-center w-[7rem] h-[4.5rem] text-sm font-bold text-gray-800 transition-colors duration-150 hover:bg-gray-100 cursor-pointer"
              aria-haspopup="menu"
              :aria-expanded="isSettingsOpen ? 'true' : 'false'"
              @click="isSettingsOpen = !isSettingsOpen"
            >
              <i class="fa-solid fa-gear mr-2"></i>
              設定
              <i class="fa-solid fa-chevron-down ml-2 text-xs"></i>
            </button>

            <div
              v-if="isSettingsOpen"
              class="fixed inset-0 z-40"
              @click="isSettingsOpen = false"
            ></div>
            <div
              v-if="isSettingsOpen"
              class="absolute right-0 z-50 mt-2 w-[14rem] rounded-md border border-gray-200 bg-white shadow-lg py-2"
            >
              <div
                class="w-full flex items-start gap-3 px-4 py-2 select-none text-xs"
              >
                <i class="fa-regular fa-building mt-0.5"></i>
                <div
                  class="flex flex-col flex-1 min-w-0 whitespace-normal break-words"
                >
                  <span class="text-sm text-gray-700 break-words">{{
                    userName
                  }}</span>
                  <span class="text-xs text-gray-500 break-words">{{
                    userEmail
                  }}</span>
                </div>
              </div>
              <button
                class="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                @click="handleLogout"
              >
                <i class="fa-solid fa-right-from-bracket"></i>
                <span>ログアウト</span>
              </button>
            </div>
          </div>

          <!-- モバイル: ハンバーガー -->
          <div class="md:hidden">
            <button
              class="flex items-center justify-center w-10 h-10 rounded hover:bg-gray-100 mr-2"
              aria-label="Open menu"
              @click="isMobileMenuOpen = !isMobileMenuOpen"
            >
              <i class="fa-solid fa-bars"></i>
            </button>
            <div
              v-if="isMobileMenuOpen"
              class="fixed inset-0 z-40 bg-black/30"
              @click="isMobileMenuOpen = false"
            ></div>
            <aside
              class="fixed right-0 top-0 z-50 h-full w-64 bg-white border-l border-gray-200 shadow-lg transform transition-transform duration-300"
              :class="isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'"
            >
              <div class="h-[4.5rem] relative">
                <button
                  class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-100"
                  aria-label="Close menu"
                  @click="isMobileMenuOpen = false"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div>
                <nav class="flex flex-col">
                  <RouterLink
                    v-for="item in menuItems"
                    :key="item.path"
                    :to="item.path"
                    class="flex items-center gap-3 px-3 h-[3.5rem] text-sm font-bold transition-colors duration-150"
                    :class="
                      isActive(item.path)
                        ? 'bg-primary text-white'
                        : 'text-gray-800 hover:bg-gray-100'
                    "
                    @click="isMobileMenuOpen = false"
                  >
                    <i :class="item.icon"></i><span>{{ item.label }}</span>
                  </RouterLink>
                </nav>
                <div class="border-t border-gray-200 my-3"></div>
                <div class="flex items-start gap-3 mb-2 px-3">
                  <i class="fa-regular fa-building mt-0.5 text-gray-600"></i>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-gray-700 break-words">
                      {{ userName }}
                    </p>
                    <p class="text-xs text-gray-500 break-words">
                      {{ userEmail }}
                    </p>
                  </div>
                </div>
                <button
                  class="flex items-center gap-3 w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-gray-700"
                  @click="handleLogout"
                >
                  <i class="fa-solid fa-right-from-bracket"></i
                  ><span>ログアウト</span>
                </button>
              </div>
            </aside>
          </div>
        </nav>
      </div>
    </div>
  </header>
  <Toast
    v-model:open="opensToast"
    :message="toastMessage"
    :variant="toastVariant"
    :duration-ms="3000"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { RouterLink, useRoute } from "vue-router";
import logoUrl from "@/assets/logo.svg";
import { useAuth } from "@/composables/useAuth";
import { isFeatureEnabled } from "@/utils/featureFlags";
import Toast from "@/components/shared/toast/Toast.vue";
import type { ToastVariant } from "@/components/shared/toast/types.d";
import { useUserStore } from "@/stores/user";

const userStore = useUserStore();
const { user, error } = storeToRefs(userStore);
const userName = computed(() => user.value?.name ?? "");
const userEmail = computed(() => user.value?.email ?? "");

const route = useRoute();
const { logout } = useAuth();

const isSettingsOpen = ref(false);
const isMobileMenuOpen = ref(false);
const opensToast = ref(false);
const toastMessage = ref("");
const toastVariant = ref<ToastVariant>("success");

watch(
  error,
  (message) => {
    if (!message) {
      return;
    }
    toastMessage.value = message;
    toastVariant.value = "error";
    opensToast.value = true;
  },
  { immediate: true },
);

const handleLogout = async () => {
  try {
    isSettingsOpen.value = false;
    isMobileMenuOpen.value = false;
    await logout();
  } catch {
    toastMessage.value = "ログアウトに失敗しました";
    toastVariant.value = "error";
    opensToast.value = true;
  }
};

const isActive = (pathPrefix: string) => route.path.startsWith(pathPrefix);

const showMatchingFlows = computed(() =>
  isFeatureEnabled("VITE_SHOW_MATCHING_FLOWS"),
);

const menuItems = computed(() => {
  const items = [
    { path: "/jobs", label: "案件一覧", icon: "fa-regular fa-file-lines" },
    {
      path: "/candidates",
      label: "求職者検索",
      icon: "fa-solid fa-magnifying-glass",
    },
  ];

  if (showMatchingFlows.value) {
    items.push({
      path: "/matching_flows",
      label: "選考管理",
      icon: "fa-regular fa-clipboard",
    });
  }

  return items;
});
</script>

<style scoped></style>
